import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Button from '../../Button';
import Row from './Row';
import { useCountdown } from './countdown';
import { fomoSurfacePath, resolveSurfaceSize } from './fomoSurface';

/**
 * FomoCard — the HeroBannerV4 FOMO card (Figma Crownstudent-MCP 90:26947 /
 * mobile 96:31294): an opaque gray-025 card with a tilted top edge (the «CurvedShape
 * Frame»), a product photo floating over its left half, «guaranteed
 * delivery» label + date, an order-deadline countdown (days : hours :
 * minutes : seconds) and a full-width CTA — the DS Button `hero` variant on
 * both breakpoints (user decision 2026-10-27).
 *
 * Internal to HeroBannerV4 (not exported from the block's index) — user
 * decision 2026-10-27. The card's box is measured (ResizeObserver) so the
 * surface path follows its height; without ResizeObserver (Jest) the Figma
 * 400×237 path stands.
 *
 * Surface layers (all `aria-hidden`), see HeroBannerV4.css:
 *  - the shadow: `.hero-v4__card-box::before`, a plain rounded box with the
 *    DS `--shadow-sticky-footer` (a box-shadow token cannot follow a path;
 *    at −8px / 20px blur a straight edge under the 6.6px tilt is invisible);
 *  - `.hero-v4__card-surface`: the gray-025 fill, `clip-path: path()`
 *    (no backdrop-filter — it made the card blink while scrolling);
 *  - `.hero-v4__card-stroke`: the 2px inner hairline, an SVG path clipped by
 *    the same path.
 */
const FomoCard = ({ fomo, reveal, delay }) => {
  const {
    image,
    imageAlt = '',
    deliveryLabel = '',
    deliveryDate = '',
    countdownLabel = '',
    deadline,
    countdownUnits = {},
    ctaLabel = '',
    ctaHref,
    ctaOnClick,
    ctaVariant = 'hero',
  } = fomo;
  const units = { days: 'd', hours: 'h', minutes: 'm', seconds: 's', ...countdownUnits };

  const boxRef = useRef(null);
  const [size, setSize] = useState(null);
  useEffect(() => {
    const el = boxRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => setSize({ width: el.offsetWidth, height: el.offsetHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const surface = resolveSurfaceSize(size?.width, size?.height);
  const path = fomoSurfacePath(surface.width, surface.height);
  const viewBox = `0 0 ${surface.width} ${surface.height}`;

  const parts = useCountdown(deadline);
  // Figma: the labels are P.Label (typo-label) in caps, the date H3, the digits H5, the units P.Label
  const digits = [
    { key: 'days', value: parts.days, unit: units.days },
    { key: 'hours', value: parts.hours, unit: units.hours },
    { key: 'minutes', value: parts.minutes, unit: units.minutes },
    { key: 'seconds', value: parts.seconds, unit: units.seconds },
  ];

  return (
    <Row reveal={reveal} delay={delay} blur={false} as="aside" className="hero-v4__card" aria-label={deliveryLabel || undefined}>
      <div className="hero-v4__card-box" ref={boxRef} style={{ '--hero-v4-card-path': `path("${path}")` }}>
        <div className="hero-v4__card-surface" aria-hidden="true" />
        <svg className="hero-v4__card-stroke" viewBox={viewBox} preserveAspectRatio="none" aria-hidden="true" focusable="false">
          <path d={path} />
        </svg>

        <div className="hero-v4__card-body">
          <div className="hero-v4__card-top">
            {image && <img className="hero-v4__cap" src={image} alt={imageAlt} />}
            <div className="hero-v4__card-info">
            {(deliveryLabel || deliveryDate) && (
              <div className="hero-v4__delivery">
                {deliveryLabel && <p className="hero-v4__delivery-label typo-label">{deliveryLabel}</p>}
                {deliveryDate && <p className="hero-v4__delivery-date typo-h3">{deliveryDate}</p>}
              </div>
            )}
            <div className="hero-v4__countdown" role="timer" aria-live="off" aria-label={countdownLabel || undefined}>
              {countdownLabel && <p className="hero-v4__countdown-label typo-label">{countdownLabel}</p>}
              <div className="hero-v4__digits">
                {digits.map(({ key, value, unit }, i) => (
                  <React.Fragment key={key}>
                    {i > 0 && <span className="hero-v4__colon typo-h6" aria-hidden="true">:</span>}
                    <span className="hero-v4__digit-group">
                      <span className="hero-v4__digit typo-h5">{value}</span>
                      <span className="hero-v4__unit typo-label">{unit}</span>
                    </span>
                  </React.Fragment>
                ))}
              </div>
            </div>
            </div>
          </div>

          {ctaLabel && (
            <Button size="primary" variant={ctaVariant} fullWidth href={ctaHref} onClick={ctaOnClick} className="hero-v4__card-cta">
              {ctaLabel}
            </Button>
          )}
        </div>
      </div>
    </Row>
  );
};

export const fomoShape = PropTypes.shape({
  /** The product photo floating over the card's left half (the cap) */
  image: PropTypes.string,
  /** Alt text of that photo */
  imageAlt: PropTypes.string,
  /** «Guaranteed delivery» label (typo-label, uppercase) */
  deliveryLabel: PropTypes.string,
  /** The delivery date, pre-formatted (typo-h3) */
  deliveryDate: PropTypes.string,
  /** «Order within» label over the countdown (typo-label, uppercase) */
  countdownLabel: PropTypes.string,
  /** Order deadline — ISO datetime, or YYYY-MM-DD (= the end of that day); missing / passed → 00 00 00 00 */
  deadline: PropTypes.string,
  /** Unit labels under the digits { days, hours, minutes, seconds } (defaults d / h / m / s) */
  countdownUnits: PropTypes.shape({
    days: PropTypes.string,
    hours: PropTypes.string,
    minutes: PropTypes.string,
    seconds: PropTypes.string,
  }),
  /** Card CTA label (Button primary, full width) */
  ctaLabel: PropTypes.string,
  /** Card CTA link target — renders it as a real link */
  ctaHref: PropTypes.string,
  /** Card CTA handler */
  ctaOnClick: PropTypes.func,
  /** Card CTA Button variant — `hero` by default: the DS conversion CTA (gradient pill, animated rim), the one HeroBannerV3 / the configurator «Nästa» wear */
  ctaVariant: PropTypes.string,
});

FomoCard.propTypes = {
  fomo: fomoShape.isRequired,
  reveal: PropTypes.bool,
  delay: PropTypes.number,
};

export default FomoCard;
